import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "reactstrap";
import Breadcrumbs from "src/components/Common/Breadcrumb";
import RichTable, { RichColumn } from "src/components/Page/RichTable";
import { useAppDispatch } from "src/store";
import {
  decideVerification,
  getDisputes,
  getFraudFlags,
  getTrustOverview,
  getTrustSuppliers,
  getVerifications,
  resolveFraudFlag,
  setDisputeStatus,
  setQualityFloor,
  setTrustTier,
} from "src/store/trust/saga";
import {
  DISPUTE_STATUS_COLOR,
  Dispute,
  FraudFlag,
  ListingVerification,
  SEVERITY_COLOR,
  TRUST_TIERS,
  TrustOverview,
  TrustSupplier,
} from "src/store/trust/type";
import { GetLocaleDate } from "src/helpers/getLocaleDate";

/**
 * Güven ve risk operasyonu — V2 Faz F.
 *
 * Rentificial Ai admin-panel'indeki trust / fraud / disputes /
 * listings-verifications ekranları buraya toplandı; o panel kalkıyor.
 *
 * Ekranın kritik noktası kalite kapısı: kapatmak tedarikçinin TÜM ürünlerini
 * aramadan düşürür. Bu yüzden hem gerekçe isteniyor hem de kaç ürünü
 * etkileyeceği butonun yanında yazıyor.
 */
type Tab = "suppliers" | "flags" | "disputes" | "verifications";

const TrustPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [overview, setOverview] = useState<TrustOverview>();
  const [suppliers, setSuppliers] = useState<TrustSupplier[]>([]);
  const [flags, setFlags] = useState<FraudFlag[]>([]);
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [verifications, setVerifications] = useState<ListingVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("suppliers");

  document.title = t("Trust.MetaTag", "Güven ve Risk | Rentificial") as string;

  const fetchAll = () => {
    setLoading(true);
    dispatch(getTrustOverview({ payload: { onSuccess: (_m: any, p: TrustOverview) => setOverview(p), onError: () => {} } }));
    dispatch(getFraudFlags({ params: "?resolved=false", payload: { onSuccess: (_m: any, p: FraudFlag[]) => setFlags(p ?? []), onError: () => {} } }));
    dispatch(getDisputes({ payload: { onSuccess: (_m: any, p: Dispute[]) => setDisputes(p ?? []), onError: () => {} } }));
    dispatch(getVerifications({ payload: { onSuccess: (_m: any, p: ListingVerification[]) => setVerifications(p ?? []), onError: () => {} } }));
    dispatch(
      getTrustSuppliers({
        payload: {
          onSuccess: (_m: any, p: TrustSupplier[]) => {
            setSuppliers(p ?? []);
            setLoading(false);
          },
          onError: () => setLoading(false),
          onFinally: () => setLoading(false),
        },
      })
    );
  };

  useEffect(fetchAll, [dispatch]);

  const changeTier = (s: TrustSupplier, tier: string) => {
    if (tier === s.TrustTier) return;
    dispatch(
      setTrustTier({
        id: s.Id,
        body: { TrustTier: tier, Note: "admin panel" },
        payload: { onSuccess: fetchAll, onError: () => {} },
      })
    );
  };

  const toggleFloor = (s: TrustSupplier) => {
    // Kapatmak ürünleri aramadan düşürür; gerekçe backend'de de zorunlu.
    let note: string | null = "";
    if (s.QualityFloorPass) {
      note = window.prompt(
        t(
          "Trust.CloseFloorPrompt",
          `Kalite kapısını kapatıyorsunuz. ${s.CardCount} ürün aramadan düşecek. Gerekçe:`
        ) as string
      );
      if (!note || !note.trim()) return;
    }
    dispatch(
      setQualityFloor({
        id: s.Id,
        body: { Pass: !s.QualityFloorPass, Note: note },
        payload: { onSuccess: fetchAll, onError: () => {} },
      })
    );
  };

  const resolveFlag = (f: FraudFlag) => {
    const res = window.prompt(t("Trust.ResolvePrompt", "Çözüm açıklaması") as string);
    if (!res || !res.trim()) return;
    dispatch(
      resolveFraudFlag({ id: f.Id, body: { Resolution: res.trim() }, payload: { onSuccess: fetchAll, onError: () => {} } })
    );
  };

  const closeDispute = (d: Dispute, status: "resolved" | "rejected") => {
    const outcome = window.prompt(t("Trust.OutcomePrompt", "Sonuç açıklaması") as string);
    if (!outcome || !outcome.trim()) return;
    dispatch(
      setDisputeStatus({
        id: d.Id,
        body: { Status: status, Outcome: outcome.trim() },
        payload: { onSuccess: fetchAll, onError: () => {} },
      })
    );
  };

  const decide = (v: ListingVerification, status: "approved" | "rejected" | "needs_info") => {
    let note: string | null = "";
    if (status !== "approved") {
      note = window.prompt(t("Trust.VerifyNotePrompt", "Gerekçe") as string);
      if (!note || !note.trim()) return;
    }
    dispatch(
      decideVerification({ id: v.Id, body: { Status: status, Note: note }, payload: { onSuccess: fetchAll, onError: () => {} } })
    );
  };

  const supplierColumns: RichColumn<TrustSupplier>[] = [
    { key: "ClientName", label: t("Trust.Supplier", "Tedarikçi") },
    {
      key: "TrustTier",
      label: t("Trust.Tier", "Güven kademesi"),
      render: (r) => (
        <select
          className="form-select form-select-sm"
          style={{ width: 130 }}
          value={r.TrustTier}
          onChange={(e) => changeTier(r, e.target.value)}
        >
          {TRUST_TIERS.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "QualityFloorPass",
      label: t("Trust.QualityFloor", "Kalite kapısı"),
      render: (r) => (
        <button
          className={`btn btn-sm btn-${r.QualityFloorPass ? "success" : "outline-danger"}`}
          onClick={() => toggleFloor(r)}
          title={
            r.QualityFloorPass
              ? (t("Trust.FloorOpenHint", "Ürünler aramada görünüyor — kapatmak için tıklayın") as string)
              : (t("Trust.FloorClosedHint", "Ürünler aramada görünmüyor — açmak için tıklayın") as string)
          }
        >
          {r.QualityFloorPass ? t("Trust.Open", "Açık") : t("Trust.Closed", "Kapalı")}
        </button>
      ),
    },
    { key: "CardCount", label: t("Trust.Cards", "Ürün") },
    {
      key: "OpenFlags",
      label: t("Trust.Flags", "Açık bayrak"),
      render: (r) => (r.OpenFlags > 0 ? <span className="badge bg-danger">{r.OpenFlags}</span> : <span className="text-muted">0</span>),
    },
    {
      key: "OpenDisputes",
      label: t("Trust.Disputes", "Açık anlaşmazlık"),
      render: (r) => (r.OpenDisputes > 0 ? <span className="badge bg-warning">{r.OpenDisputes}</span> : <span className="text-muted">0</span>),
    },
  ];

  const flagColumns: RichColumn<FraudFlag>[] = [
    {
      key: "Severity",
      label: t("Trust.Severity", "Şiddet"),
      render: (r) => <span className={`badge bg-${SEVERITY_COLOR[r.Severity] ?? "secondary"}`}>{r.Severity}</span>,
    },
    { key: "RuleCode", label: t("Trust.Rule", "Kural") },
    { key: "EntityType", label: t("Trust.Entity", "Nesne") },
    { key: "ClientName", label: t("Trust.Supplier", "Tedarikçi") },
    { key: "CreateTime", label: t("Trust.Created", "Tarih"), render: (r) => GetLocaleDate(r.CreateTime) },
  ];

  const disputeColumns: RichColumn<Dispute>[] = [
    {
      key: "Status",
      label: t("Trust.Status", "Durum"),
      render: (r) => (
        <>
          <span className={`badge bg-${DISPUTE_STATUS_COLOR[r.Status] ?? "secondary"}`}>{r.Status}</span>
          {r.Overdue && <span className="badge bg-danger ms-1">SLA</span>}
        </>
      ),
    },
    { key: "Reason", label: t("Trust.Reason", "Sebep") },
    { key: "ClientName", label: t("Trust.Supplier", "Tedarikçi") },
    { key: "AmountClaimed", label: t("Trust.Amount", "Talep") },
    { key: "SlaDueAt", label: "SLA", render: (r) => (r.SlaDueAt ? GetLocaleDate(r.SlaDueAt) : "—") },
  ];

  const verificationColumns: RichColumn<ListingVerification>[] = [
    { key: "AssetCardName", label: t("Trust.Listing", "İlan") },
    { key: "ClientName", label: t("Trust.Supplier", "Tedarikçi") },
    { key: "Status", label: t("Trust.Status", "Durum"), render: (r) => <span className="badge bg-secondary">{r.Status}</span> },
    { key: "CheckType", label: t("Trust.CheckType", "Kontrol") },
    { key: "SlaDueAt", label: "SLA", render: (r) => (r.SlaDueAt ? GetLocaleDate(r.SlaDueAt) : "—") },
  ];

  const Kpi = ({ label, value, tone }: { label: string; value: number; tone?: string }) => (
    <div className="card mb-0" style={{ minWidth: 160, flex: "1 1 160px" }}>
      <div className="card-body py-3">
        <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
        <div className={`fw-bold ${tone && value > 0 ? "text-" + tone : ""}`} style={{ fontSize: 24 }}>{value}</div>
      </div>
    </div>
  );

  const tabs: Array<{ id: Tab; label: string; badge?: number }> = [
    { id: "suppliers", label: t("Trust.TabSuppliers", "Tedarikçiler") as string },
    { id: "flags", label: t("Trust.TabFlags", "Bayraklar") as string, badge: flags.length },
    { id: "disputes", label: t("Trust.TabDisputes", "Anlaşmazlıklar") as string, badge: disputes.filter((d) => !d.ClosedAt).length },
    { id: "verifications", label: t("Trust.TabVerifications", "İlan doğrulama") as string, badge: verifications.filter((v) => v.Status === "pending").length },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <Breadcrumbs title={t("Breadcrumb.Dashboard")} breadcrumbItem={t("Trust.PageTitle", "Güven ve Risk") as string} />
            <button className="btn btn-warning mb-3" onClick={fetchAll}>
              <i className="mdi mdi-refresh" />
            </button>
          </div>

          {overview && (
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Kpi label={t("Trust.OpenFlags", "Açık bayrak") as string} value={overview.OpenFlags} tone="danger" />
              <Kpi label={t("Trust.UrgentFlags", "Acil bayrak") as string} value={overview.UrgentFlags} tone="danger" />
              <Kpi label={t("Trust.OpenDisputes", "Açık anlaşmazlık") as string} value={overview.OpenDisputes} tone="warning" />
              <Kpi label={t("Trust.Overdue", "SLA geçmiş") as string} value={overview.OverdueDisputes} tone="danger" />
              <Kpi label={t("Trust.PendingVerifications", "Bekleyen doğrulama") as string} value={overview.PendingVerifications} tone="warning" />
              <Kpi label={t("Trust.BelowFloor", "Kapı kapalı tedarikçi") as string} value={overview.BelowQualityFloor} tone="danger" />
            </div>
          )}

          <ul className="nav nav-pills mb-3 gap-1">
            {tabs.map((x) => (
              <li className="nav-item" key={x.id}>
                <button type="button" className={`nav-link ${tab === x.id ? "active" : ""}`} onClick={() => setTab(x.id)}>
                  {x.label}
                  {!!x.badge && <span className="badge bg-danger ms-1">{x.badge}</span>}
                </button>
              </li>
            ))}
          </ul>

          {tab === "suppliers" && (
            <RichTable<TrustSupplier>
              tableId="trust-suppliers" t={t as any} columns={supplierColumns} rows={suppliers}
              loading={loading} rowKey={(r) => r.Id}
              emptyText={t("Trust.Empty", "Kayıt yok") as string}
            />
          )}

          {tab === "flags" && (
            <RichTable<FraudFlag>
              tableId="trust-flags" t={t as any} columns={flagColumns} rows={flags}
              loading={loading} rowKey={(r) => r.Id}
              emptyText={t("Trust.NoFlags", "Açık bayrak yok") as string}
              actions={(r) => (
                <button className="btn btn-sm btn-success" onClick={() => resolveFlag(r)}>
                  {t("Trust.Resolve", "Çöz")}
                </button>
              )}
            />
          )}

          {tab === "disputes" && (
            <RichTable<Dispute>
              tableId="trust-disputes" t={t as any} columns={disputeColumns} rows={disputes}
              loading={loading} rowKey={(r) => r.Id}
              emptyText={t("Trust.NoDisputes", "Anlaşmazlık yok") as string}
              actions={(r) =>
                r.ClosedAt ? (
                  <span className="text-muted">{t("Trust.Closed2", "kapalı")}</span>
                ) : (
                  <>
                    <button className="btn btn-sm btn-success me-1" onClick={() => closeDispute(r, "resolved")}>
                      {t("Trust.Accept", "Çözüldü")}
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => closeDispute(r, "rejected")}>
                      {t("Trust.Reject", "Reddet")}
                    </button>
                  </>
                )
              }
            />
          )}

          {tab === "verifications" && (
            <RichTable<ListingVerification>
              tableId="trust-verifications" t={t as any} columns={verificationColumns} rows={verifications}
              loading={loading} rowKey={(r) => r.Id}
              emptyText={t("Trust.NoVerifications", "Bekleyen doğrulama yok") as string}
              actions={(r) =>
                r.Status === "pending" ? (
                  <>
                    <button className="btn btn-sm btn-success me-1" onClick={() => decide(r, "approved")}>
                      <i className="mdi mdi-check" />
                    </button>
                    <button className="btn btn-sm btn-outline-warning me-1" onClick={() => decide(r, "needs_info")}>
                      <i className="mdi mdi-help" />
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => decide(r, "rejected")}>
                      <i className="mdi mdi-close" />
                    </button>
                  </>
                ) : (
                  <span className="text-muted">{r.Status}</span>
                )
              }
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TrustPage;
